import React, { useState, useEffect } from 'react';
import { useDaumPostcodePopup } from 'react-daum-postcode';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { Box, Button, Grid, Typography } from '@mui/material';
import { getCoordinatesByAddress } from '../../api/nominatium';
import { accountStatus, businessDivision, planTypes } from '../../constants/constants';
import { FormInputPhone } from '../../form-components/FormInputPhone';
import { FormInputRadio } from '../../form-components/FormInputRadio';
import { FormInputText } from '../../form-components/FormInputText';
import { FormInputBRN } from '../../form-components/FormInputBRN';
import AnimationPage from '../../pages/AnimationPage';
import { errorMessages, isKoreanOnly } from '../../utils/methods';
import { createCenterManager } from '../../api/member-management';

export default function CreateManager() {
  const token = useSelector(function (state) {
    return state.user.user.token;
  });
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');

  const { handleSubmit, control, setValue, setError, getValues } = useForm({});

  const scriptUrl = 'https://t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  const open = useDaumPostcodePopup(scriptUrl);

  const handleComplete = async data => {
    setCity(data.sigungu);
    // 사용자가 선택한 주소 타입에 따라 도로명/지번 주소 설정
    const selectedAddress = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
    setValue('address', selectedAddress);
    setPostalCode(data.zonecode);

    await getCoordinatesByAddress(data.addressEnglish).then(data => {
      setValue('latitude', data[0].coordinate.latitude);
      setValue('longitude', data[0].coordinate.longitude);
    });
  };

  const handleClick = () => {
    open({ onComplete: handleComplete });
  };

  const submit = async values => {
    // if (!isKoreanOnly(values.name)) {
    //   toast.error('이름은 한글만 가능합니다');
    //   return;
    // }

    if (values.password !== values.verifyPassword) {
      return setError('verifyPassword', {
        type: 'custom',
        message: t('Password not matched'),
      });
    }

    const data = {
      managerInfo: {
        username: values.username,
        name: values.name,
        password: values.password,
        phoneNumber: values.phoneNumber,
        email: values.email,
      },
      centerInfo: {
        businessName: values.businessName,
        businessNumber: values.businessNumber,
        businessClassification: values.businessClassification,
        businessType: values.businessType,
        postalCode: postalCode,
        city: city,
        address: values.address,
        addressDetail: values.addressDetail,
        representativeNumber: values.representativeNumber,
        email: values.email_1,
        homepage: values.homepage,
        latitude: values.latitude,
        longitude: values.longitude,
        planType: values.planType,
      },
      status: values.status,
    };

    try {
      await createCenterManager(token, data);
      toast.success(t('Created successfully'));
      navigate('/corp');
    } catch (error) {
      toast.error(errorMessages(error));
    }
  };

  useEffect(() => {
    setValue('businessClassification', 'INDIVIDUAL');
    setValue('status', 'APPROVED');
    setValue('planType', 'LITE');
  }, []);

  return (
    <AnimationPage>
      <Box sx={{ background: 'white', borderRadius: '5px', width: '1080px', px: 2.6, py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Link to="/corp" style={{ textDecoration: 'none', color: '#3a3a3a', display: 'flex' }}>
            <img src="/images/backIcon.svg" alt="backIcon" />
            <Typography sx={{ fontSize: '18px', lineHeight: '24px', ml: '8px', fontWeight: 700 }}>
              {t('Corporate Member great')}
            </Typography>
          </Link>

          <Link to="/corp" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <Typography
              sx={{
                ml: 0.75,
                color: '#6B7280',
                fontSize: '14px',
                fontWeight: 500,
                lineHeight: '16px',
                color: '#0275FC',
              }}>
              {t('List')}
            </Typography>
          </Link>
        </Box>
        <hr />

        <Box sx={{ display: 'flex', alignItems: 'center', mt: '30px' }}>
          <Typography sx={{ width: '30%', fontSize: '16px', fontWeight: 500, mr: '10px' }}>
            {t('Company information')}
          </Typography>
        </Box>

        <Grid container columnSpacing={9} sx={{ mt: 3 }}>
          <Grid item xs={6}>
            <Box className="detail-form-box">
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Name*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  control={control}
                  name="name"
                  placeholder={t('Please enter your name')}
                  rules={{ required: true }}
                />
              </Grid>
            </Box>
            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('ID*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="username"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('Please enter your ID')}
                  autoComplete="off"
                />
              </Grid>
            </Box>

            <Box className="detail-form-box">
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Password*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  control={control}
                  type="password"
                  autoComplete="new-password"
                  sx={{ background: '#fff', borderRadius: '4px' }}
                  name="password"
                  placeholder={t(
                    'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                  )}
                  rules={{
                    required: true,
                    pattern: {
                      value: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[~!@#$%^&*()_=])[A-Za-z\d~!@#$%^&*()_=]{8,16}$/,
                      message: t(
                        'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                      ),
                    },
                  }}
                />
              </Grid>
            </Box>
            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Confirm Password*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  control={control}
                  name="verifyPassword"
                  autoComplete="new-password"
                  sx={{ color: '#9A9A9A', background: '#fff' }}
                  placeholder={t(
                    'Please enter your password (8 to 16 characters including English letters, numbers, and special characters)',
                  )}
                  type="password"
                  rules={{
                    required: t('Password not matched'),
                    validate: value => value === getValues('password') || t('Password not matched'),
                  }}
                />
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={6}>
            <Box className="detail-form-box">
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Phone number*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputPhone
                  control={control}
                  setValue={setValue}
                  name="phoneNumber"
                  rules={{ required: true }}
                  placeholder={t('Enter the phone number')}
                />
              </Grid>
              {/* <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    ml: 2,
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    '&:hover': { color: '#fff' },
                  }}>
                  {t('SENT')}
                </Button>
              </Grid> */}
            </Box>

            {/* <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('Authentication Verification')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="certification"
                  control={control}
                  placeholder={t('Please enter your verification number')}
                />
              </Grid>
              {/* <Grid item xs={2}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    ml: 2,
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    '&:hover': { color: '#fff' },
                  }}>
                  {t('Authentication')}
                </Button>
              </Grid>
            </Box> */}
            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-label">{t('E-mail*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="email"
                  control={control}
                  type="email"
                  autoComplete="off"
                  rules={{ required: true }}
                  placeholder={t('Please enter your email')}
                />
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <hr />
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '30px' }}>
          <Typography sx={{ width: '30%', fontSize: '16px', fontWeight: 500, mr: '10px' }}>
            {t('Business Information')}
          </Typography>
        </Box>
        <Grid container columnSpacing={9} sx={{ my: 5 }}>
          <Grid item xs={6}>
            <Box className="detail-form-box">
              <Grid item xs={2}>
                <Typography className="detail-form-box">{t('Business Name*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="businessName"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('Please enter your business name')}
                />
              </Grid>
            </Box>

            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-box">{t('Employer Identification Number*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputBRN
                  name="businessNumber"
                  setValue={setValue}
                  rules={{ required: true }}
                  control={control}
                  placeholder={t('Please enter your business number')}
                />
              </Grid>
            </Box>

            <Box className="detail-form-box">
              <Grid item xs={2}>
                <Typography className="detail-form-box">{t('Employer Type')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputRadio
                  rules={{ required: true }}
                  name="businessClassification"
                  control={control}
                  options={businessDivision}
                  row
                />
              </Grid>
            </Box>
            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-box">{t('Type of Business*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="businessType"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('Please enter the industry/type of business')}
                />
              </Grid>
            </Box>

            <Box className="detail-form-box" my={3.25}>
              <Grid item xs={2}>
                <Typography className="detail-form-box">{t('Mailing Address*')}</Typography>
              </Grid>
              <Grid item xs={8}>
                <FormInputText
                  name="address"
                  control={control}
                  rules={{ required: true }}
                  placeholder={t('Please enter your address')}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  onClick={handleClick}
                  variant="contained"
                  color="primary"
                  sx={{
                    height: 40,
                    bgcolor: '#F8FBFB',
                    boxShadow: 'none',
                    color: '#0F7F85',
                    fontSize: '13px',
                    fontWeight: 500,
                    lineHeight: '15.51px',
                    ml: 0,
                    width: '100%',
                    '&:hover': { color: '#fff' },
                  }}>
                  {t('Search')}
                </Button>
              </Grid>
            </Box>

            <Box className="detail-form-box">
              <Grid item xs={2}></Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="addressDetail"
                  control={control}
                  placeholder={t('Please enter your detailed address')}
                />
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box className="detail-form-box">
              <Grid item xs={2.5}>
                <Typography className="detail-form-box">{t('Representative number*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputPhone
                  control={control}
                  setValue={setValue}
                  rules={{ required: true }}
                  name="representativeNumber"
                  placeholder={t('Please enter your representative number')}
                  isLandline
                />
              </Grid>
            </Box>

            <Box className="detail-form-box" sx={{ display: 'flex', alignItems: 'center', my: 3.25 }}>
              <Grid item xs={2.5}>
                <Typography className="detail-form-box">{t('E-mail*')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText
                  name="email_1"
                  control={control}
                  type="email"
                  rules={{ required: true }}
                  placeholder={t('Please enter your email')}
                />
              </Grid>
            </Box>
            <Box className="detail-form-box" sx={{ display: 'flex', alignItems: 'center', my: 3.25 }}>
              <Grid item xs={2.5}>
                <Typography className="detail-form-box">{t('Homepage')}</Typography>
              </Grid>
              <Grid item xs={10}>
                <FormInputText name="homepage" control={control} placeholder={t('Home page URL')} />
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <hr />
        <Box sx={{ display: 'flex', my: 4 }}>
          <Typography className="detail-form-box" sx={{ width: 70, mr: 3 }}>
            {t('Account Status')}
          </Typography>

          <FormInputRadio name="status" control={control} options={accountStatus} row />
        </Box>
        <hr />
        <Box sx={{ display: 'flex', my: 4 }}>
          <Typography className="detail-form-box" sx={{ width: 100, mr: 3 }}>
            {t('Plan Type')}
          </Typography>

          <FormInputRadio name="planType" control={control} options={planTypes} row />
        </Box>
        <hr />
        <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
          <Button
            href="/corp"
            variant="contained"
            sx={{
              borderRadius: '5px',
              minWidth: '103px',
              height: '40px',
              bgcolor: '#98A6AD',
              color: '#fff',
              '&:hover': { background: '#ddd' },
              fontSize: '13px',
              fontWeight: 700,
            }}>
            {t('Cancel')}
          </Button>
          <Button
            id="store-create-button"
            onClick={handleSubmit(submit)}
            // disabled={user.userPoint}
            variant="contained"
            color="primary"
            sx={{ borderRadius: '5px', minWidth: '183px', height: '40px', ml: 1.25 }}>
            {t('Save')}
          </Button>
        </Box>
      </Box>
    </AnimationPage>
  );
}
